import React, { useState, useEffect } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { motion } from "framer-motion";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { MapPin, Users, Calendar, Mail, Globe, University, Heart, School } from "lucide-react";
import { supabase } from "../../../src/integrations/supabase/client";
import usStatesData from "@/assets/us-states-10m.json";
import ofuesiLogo from "@/assets/ofuesi-logo.png";

// Regional color mapping using design system tokens
const regionColors = {
  Northwest: "hsl(var(--ministry-purple))",
  Midwest: "hsl(var(--ministry-gold))",
  Eastern: "hsl(var(--ministry-rose))",
  Southeast: "hsl(var(--ministry-emerald))",
  Southern: "hsl(var(--ministry-deep))",
  Southwest: "hsl(var(--ministry-cyan))",
};

const stateToRegion: Record<string, string> = {
  // Northwest - WA, OR, ID, MT, WY, AK
  Washington: "Northwest",
  Oregon: "Northwest",
  Idaho: "Northwest",
  Montana: "Northwest",
  Wyoming: "Northwest",
  Alaska: "Northwest",

  // Southwest - CO, NM, AZ, UT, NV, CA, HI
  California: "Southwest",
  Nevada: "Southwest",
  Utah: "Southwest",
  Arizona: "Southwest",
  Hawaii: "Southwest",

  // Midwest - ND, SD, NE, KS, MN, IA, MO, WI, IL, MI, IN, OH
  "North Dakota": "Midwest",
  "South Dakota": "Midwest",
  Nebraska: "Midwest",
  Kansas: "Midwest",
  Minnesota: "Midwest",
  Iowa: "Midwest",
  Missouri: "Midwest",
  Wisconsin: "Midwest",
  Illinois: "Midwest",
  Michigan: "Midwest",
  Indiana: "Midwest",
  Ohio: "Midwest",
  Kentucky: "Midwest",

  // Southern - KY, TN, GA, AL, MS, LA, AR, OK, TX
  Colorado: "Southern",
  "New Mexico": "Southern",
  Oklahoma: "Southern",
  Texas: "Southern",
  Arkansas: "Southern",
  Louisiana: "Southern",

  // Eastern - ME, NH, VT, MA, RI, CT, NY, NJ, PA
  Maine: "Eastern",
  "New Hampshire": "Eastern",
  Vermont: "Eastern",
  Massachusetts: "Eastern",
  "Rhode Island": "Eastern",
  Connecticut: "Eastern",
  "New York": "Eastern",
  "New Jersey": "Eastern",
  Pennsylvania: "Eastern",
  Delaware: "Eastern",
  Maryland: "Eastern",
  Virginia: "Eastern",
  "District of Columbia": "Eastern",
  "West Virginia": "Eastern",

  // Southeast - DE, MD, VA, WV, NC, SC, FL, DC
  Tennessee: "Southeast",
  Mississippi: "Southeast",
  Alabama: "Southeast",
  Georgia: "Southeast",
  "North Carolina": "Southeast",
  "South Carolina": "Southeast",
  Florida: "Southeast",
};

// State center coordinates for placing OFUESI logos
const stateCenters: Record<string, [number, number]> = {
  California: [-119.4179, 36.7783],
  Texas: [-99.9018, 31.9686],
  Florida: [-81.5158, 27.6648],
  "New York": [-74.2179, 43.2994],
  Pennsylvania: [-77.1945, 41.2033],
  Illinois: [-89.3985, 40.6331],
  Ohio: [-82.7649, 40.2732],
  Georgia: [-83.2471, 32.1656],
  "North Carolina": [-78.6569, 35.7596],
  Michigan: [-84.5361, 44.3467],
  "New Jersey": [-74.7429, 40.0583],
  Virginia: [-78.6569, 37.4316],
  Washington: [-120.7401, 47.7511],
  Arizona: [-111.0937, 34.0489],
  Massachusetts: [-71.0275, 42.2373],
  Tennessee: [-86.7816, 35.7176],
  Indiana: [-86.1349, 40.2732],
  Missouri: [-91.8318, 38.5767],
  Maryland: [-76.6413, 39.0458],
  Wisconsin: [-89.6165, 43.7844],
  Colorado: [-105.3111, 39.0598],
  Minnesota: [-93.9196, 46.3954],
  "South Carolina": [-81.1637, 33.8361],
  Alabama: [-86.7916, 32.3668],
  Louisiana: [-91.8612, 30.9739],
  Kentucky: [-84.8823, 37.8393],
  Oregon: [-120.5542, 43.8041],
  Oklahoma: [-96.9289, 35.5653],
  Connecticut: [-72.7273, 41.5978],
  Utah: [-111.891, 39.321],
  Iowa: [-93.6091, 41.878],
  Nevada: [-117.0554, 38.3135],
  Arkansas: [-92.3731, 34.9513],
  Mississippi: [-89.6678, 32.7673],
  Kansas: [-96.7265, 38.5111],
  "New Mexico": [-106.2485, 34.8405],
  Nebraska: [-99.9018, 41.4925],
  "West Virginia": [-80.9696, 38.468],
  Idaho: [-114.742, 44.2394],
  Hawaii: [-157.4983, 21.1098],
  "New Hampshire": [-71.5491, 43.4525],
  Maine: [-69.3924, 44.6939],
  Montana: [-110.3626, 46.9048],
  "Rhode Island": [-71.5118, 41.6809],
  Delaware: [-75.5277, 39.3185],
  "South Dakota": [-99.9018, 44.2998],
  "North Dakota": [-101.002, 47.5362],
  Alaska: [-152.4044, 61.3707],
  Vermont: [-72.7093, 44.0459],
  Wyoming: [-107.3025, 42.7475],
  "District of Columbia": [-77.0145, 38.9046],
};

// Mapping between US state names and their 2-letter codes
const stateNameToCode: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
  "District of Columbia": "DC",
};

const codeToStateName: Record<string, string> = Object.fromEntries(
  Object.entries(stateNameToCode).map(([name, code]) => [code, name]),
);

interface EnhancedInteractiveMapProps {
  showStatsHeader?: boolean;
  title?: string;
  description?: string;
}

const EnhancedInteractiveMap: React.FC<EnhancedInteractiveMapProps> = ({
  showStatsHeader = false,
  title = "Active OFUESI Regions",
  description = "Click on any highlighted state to explore regional activities",
}) => {
  const [selectedRegion, setSelectedRegion] = useState<any>(null);
  const [regions, setRegions] = useState<any[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [hoverCoords, setHoverCoords] = useState<[number, number] | null>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRegionData();
  }, []);

  const loadRegionData = async () => {
    try {
      setLoading(true);
      const { data: regionData, error } = await supabase.from("regions").select("*");

      if (error) {
        console.error("Error loading region data:", error);
        setRegions([]);
        return;
      }

      // Parse bible_studies JSON strings into objects
      const parsed = (regionData || []).map((r: any) => {
        if (Array.isArray(r.bible_studies)) {
          r.bible_studies = r.bible_studies
            .map((s: any) => {
              if (typeof s === "string") {
                try {
                  return JSON.parse(s);
                } catch {
                  return null;
                }
              }
              return s;
            })
            .filter(Boolean);
        }
        return r;
      });

      setRegions(parsed);
    } catch (error) {
      console.error("Error loading region data:", error);
      setRegions([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedRegionData = async (regionName: string) => {
    const { data, error } = await supabase
      .from("regions")
      .select(
        `
        *,
        activities:activities(
          id,
          title,
          description,
          activity_type,
          schedule,
          start_date,
          start_time,
          end_time,
          has_zoom_meeting,
          zoom_link,
          passcode
        ),
        conferences:conferences(
          id,
          conference_name,
          conference_date,
          conference_dates,
          location,
          description,
          registration_url,
          flyer_url,
          status
        )
      `,
      )
      .eq("name", regionName)
      .maybeSingle();

    if (error) {
      console.error("Error loading region data:", error);
      return null;
    }

    // Parse bible_studies if present
    if (data && Array.isArray(data.bible_studies)) {
      data.bible_studies = data.bible_studies
        .map((s: any) => {
          if (typeof s === "string") {
            try {
              return JSON.parse(s);
            } catch {
              return null;
            }
          }
          return s;
        })
        .filter(Boolean);
    }

    // Fetch actual member count for this region
    if (data) {
      const { count: memberCount } = await supabase
        .from("members")
        .select("id", { count: "exact", head: true })
        .eq("region_id", data.id)
        .eq("is_active", true);

      data.total_members = memberCount || 0;

      // Fetch actual university count with fellowship for this region
      const { count: universityCount } = await supabase
        .from("universities")
        .select("id", { count: "exact", head: true })
        .eq("region_id", data.id)
        .eq("has_fellowship", true);

      data.universities = universityCount || 0;

      // Calculate prayer cells from the prayer_cells array
      data.active_prayer_cells = Array.isArray(data.prayer_cells) ? data.prayer_cells.length : 0;

      // Fetch all five regional leadership roles from member_roles table
      const { data: coordinatorData } = await supabase
        .from("member_roles")
        .select(
          `
          role,
          members!inner(
            full_name,
            email,
            phone,
            profile_image_url,
            region_id,
            is_active
          )
        `,
        )
        .eq("members.region_id", data.id)
        .in("role", [
          "regional_president",
          "regional_vice_president",
          "regional_secretary",
          "regional_joint_secretary",
          "regional_treasurer",
        ])
        .eq("members.is_active", true);

      // Role priority for sorting
      const rolePriority: Record<string, number> = {
        regional_president: 1,
        regional_vice_president: 2,
        regional_secretary: 3,
        regional_joint_secretary: 4,
        regional_treasurer: 5,
      };

      // Transform and sort coordinator data
      const transformedCoordinators = (coordinatorData || [])
        .map((item) => ({
          ...item.members,
          role: item.role,
        }))
        .sort((a, b) => (rolePriority[a.role] || 999) - (rolePriority[b.role] || 999));

      if (transformedCoordinators.length > 0) {
        data.coordinators = transformedCoordinators;
        // First coordinator in sorted list is the primary one
        data.coordinator_profile = transformedCoordinators[0];
      }

      // Fetch universities with fellowship
      const { data: universitiesData } = await supabase
        .from("universities")
        .select("*")
        .eq("region_id", data.id)
        .eq("has_fellowship", true);

      if (universitiesData) {
        data.universities_list = universitiesData;
      }
    }

    return data;
  };

  const handleStateClick = async (geo: any, forceActiveState = false) => {
    const stateName = geo.properties.name || geo.properties.NAME;
    const region = stateToRegion[stateName];

    if (region) {
      const regionData = regions.find((r) => r.name === region || r.name.toLowerCase() === region.toLowerCase());

      // Load detailed region data with activities and conferences
      if (regionData) {
        const detailedData = await loadDetailedRegionData(regionData.name);
        // Check if state is active using both prayer_cells and aggregated data
        const activeStatesForRegion = activeStatesByRegion[regionData.name] || [];
        const isActiveState =
          forceActiveState ||
          detailedData?.prayer_cells?.some((s: string) => s.toLowerCase() === stateName.toLowerCase()) ||
          activeStatesForRegion.some((s: string) => s.toLowerCase() === stateName.toLowerCase());
        setSelectedRegion({ ...(detailedData || regionData), hasMarker: isActiveState, clickedState: stateName });
      } else {
        setSelectedRegion({ name: region, hasMarker: forceActiveState, clickedState: stateName });
      }

      setModalOpen(true);
    }
  };

  const getStateFill = (geo: any) => {
    const stateName = geo.properties.name || geo.properties.NAME;
    const region = stateToRegion[stateName];

    if (region && regionColors[region as keyof typeof regionColors]) {
      return regionColors[region as keyof typeof regionColors];
    }
    return "hsl(var(--muted))";
  };

  const getStateOpacity = (geo: any) => {
    const stateName = geo.properties.name || geo.properties.NAME;
    const region = stateToRegion[stateName];
    return region ? 0.8 : 0.3;
  };

  // Get active states for each region from prayer_cells and bible_studies
  const getRegionActiveStates = () => {
    const activeStates: Record<string, string[]> = {};

    regions.forEach((region) => {
      const set = new Set<string>();

      // Add from prayer_cells (state names)
      if (Array.isArray(region.prayer_cells)) {
        region.prayer_cells.forEach((s: string) => {
          const match = Object.keys(stateNameToCode).find((n) => n.toLowerCase() === String(s).toLowerCase());
          if (match) set.add(match);
        });
      }

      // Add from bible_studies (state or code)
      if (Array.isArray(region.bible_studies)) {
        region.bible_studies.forEach((study: any) => {
          const raw = String(study?.state || study?.state_code || "").trim();
          if (!raw) return;
          const full =
            codeToStateName[raw.toUpperCase()] ||
            Object.keys(stateNameToCode).find((n) => n.toLowerCase() === raw.toLowerCase());
          if (full) set.add(full);
        });
      }

      if (set.size > 0) {
        activeStates[region.name] = Array.from(set);
      }
    });

    return activeStates;
  };

  const activeStatesByRegion = getRegionActiveStates();

  return (
    <div className="w-full bg-transparent">
      {/* Interactive Map */}
      <motion.div
        className="relative rounded-2xl overflow-hidden shadow-divine p-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h3 className="text-2xl font-bold text-foreground mb-2">{title}</h3>
          <p className="text-muted-foreground">{description}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <ComposableMap
            projection="geoAlbersUsa"
            width={900}
            height={500}
            projectionConfig={{
              scale: 1100,
            }}
          >
            <Geographies geography={usStatesData}>
              {({ geographies }) =>
                geographies
                  .filter((geo) => geo.properties.name || geo.properties.NAME)
                  .map((geo) => {
                    const stateName = geo.properties.name || geo.properties.NAME;
                    const region = stateToRegion[stateName];
                    const isActive = !!region;

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onClick={() => isActive && handleStateClick(geo)}
                        onMouseMove={(e) => {
                          setHoveredState(stateName);
                          setHoverCoords([e.clientX, e.clientY]);
                        }}
                        onMouseLeave={() => {
                          setHoveredState(null);
                          setHoverCoords(null);
                        }}
                        style={{
                          default: {
                            fill: getStateFill(geo),
                            fillOpacity: getStateOpacity(geo),
                            stroke: "hsl(var(--border))",
                            strokeWidth: 0.5,
                            outline: "none",
                          },
                          hover: {
                            fill: isActive ? "hsl(var(--primary))" : getStateFill(geo),
                            fillOpacity: isActive ? 0.9 : 0.4,
                            stroke: "hsl(var(--primary))",
                            strokeWidth: isActive ? 2 : 0.5,
                            outline: "none",
                            cursor: isActive ? "pointer" : "default",
                          },
                          pressed: {
                            fill: isActive ? "hsl(var(--primary-glow))" : getStateFill(geo),
                            fillOpacity: 0.9,
                            stroke: "hsl(var(--primary))",
                            strokeWidth: 2,
                            outline: "none",
                          },
                        }}
                      />
                    );
                  })
              }
            </Geographies>

            {/* OFUESI Logo Markers for Active States - Only show states selected in region management */}
            {Object.entries(activeStatesByRegion).flatMap(([regionName, stateNames]) => {
              const regionData = regions.find((r) => r.name === regionName);
              const regionStates = regionData?.states || [];

              return stateNames
                .filter((stateName) => regionStates.includes(stateName))
                .map((stateName, index) => {
                  const coordinates = stateCenters[stateName];
                  if (!coordinates) return null;

                  return (
                    <Marker key={`${regionName}-${stateName}`} coordinates={coordinates}>
                      <motion.g
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                        whileHover={{ scale: 1.2 }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          // Find the geography object for this state to reuse handleStateClick
                          const geoForState = { properties: { name: stateName } };
                          await handleStateClick(geoForState, true);
                        }}
                        style={{ cursor: "pointer", pointerEvents: "all" }}
                      >
                        <image
                          href={ofuesiLogo}
                          x={-12}
                          y={-12}
                          width={32}
                          height={32}
                          style={{ borderRadius: "50%", pointerEvents: "all" }}
                        />
                      </motion.g>
                    </Marker>
                  );
                })
                .filter(Boolean);
            })}
          </ComposableMap>

          {/* Cursor-following popup */}
          {hoveredState && hoverCoords && (
            <div
              className="fixed z-50 pointer-events-none"
              style={{
                left: `${hoverCoords[0] + 15}px`,
                top: `${hoverCoords[1] - 40}px`,
              }}
            >
              <motion.div
                className="bg-white/95 dark:bg-gray-900/95 border rounded-lg p-2 shadow-lg backdrop-blur-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.15 }}
              >
                <p className="text-xs font-medium">{hoveredState}</p>
                <p className="text-[10px] text-muted-foreground">{stateToRegion[hoveredState]} Region</p>
              </motion.div>
            </div>
          )}
        </motion.div>

        {/* Loading State */}
        {loading && (
          <motion.div
            className="absolute inset-0 bg-background/50 backdrop-blur-sm flex items-center justify-center rounded-2xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading regions...</p>
            </div>
          </motion.div>
        )}

        {/* Regional Legend */}
        <motion.div
          className="mt-6 flex flex-wrap gap-3 justify-center"
          // initial={{ opacity: 0, y: 20 }}
          //animate={{ opacity: 1, y: 0 }}
          //transition={{ duration: 0.6, delay: 0.6 }}
        >
          {Object.entries(regionColors).map(([region, color]) => (
            <Badge
              variant="outline"
              className="flex items-center space-x-2 py-2 px-4 transition-all duration-300"
              style={{ borderColor: color }}
            >
              <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: color }} />
              <span className="text-sm font-medium">{region}</span>
            </Badge>
          ))}
        </motion.div>
      </motion.div>

      {/* Regional Information Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-primary flex items-center gap-2">
              <MapPin className="w-6 h-6" />
              {selectedRegion?.clickedState && selectedRegion.clickedState !== selectedRegion?.name
                ? `${selectedRegion.clickedState}, ${selectedRegion?.name || selectedRegion} Region`
                : `${selectedRegion?.name || selectedRegion} Region`}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {(() => {
              const regionData =
                typeof selectedRegion === "object" ? selectedRegion : regions.find((r) => r.name === selectedRegion);

              const hasMarker = selectedRegion?.hasMarker;
              const clickedState = selectedRegion?.clickedState;

              // Filter bible studies for the clicked state if available
              let filteredBibleStudies = regionData?.bible_studies || [];
              if (clickedState && filteredBibleStudies.length > 0) {
                const clickedLower = clickedState.toLowerCase();
                const clickedCode = (stateNameToCode[clickedState] || "").toUpperCase();
                const clickedCodeLower = clickedCode.toLowerCase();
                filteredBibleStudies = filteredBibleStudies.filter((study: any) => {
                  const stateVal = String(study.state || "").toLowerCase();
                  const stateCodeVal = String(study.state_code || "").toLowerCase();
                  const cityValUpper = String(study.city || "").toUpperCase();
                  // Match by explicit state fields, or by state name/code present in the city string (e.g., "Philadelphia, PA")
                  const cityHasCode =
                    clickedCode &&
                    (cityValUpper.includes(`, ${clickedCode}`) ||
                      cityValUpper.includes(` ${clickedCode}`) ||
                      cityValUpper.includes(`(${clickedCode})`));
                  const cityHasName = cityValUpper.includes(clickedLower.toUpperCase());
                  return (
                    stateVal === clickedLower ||
                    stateVal === clickedCodeLower ||
                    stateCodeVal === clickedCodeLower ||
                    cityHasCode ||
                    cityHasName
                  );
                });
              }

              // Filter universities for the clicked state
              let filteredUniversities = regionData?.universities_list || [];
              if (clickedState && filteredUniversities.length > 0) {
                filteredUniversities = filteredUniversities.filter(
                  (university: any) => university.state?.toLowerCase() === clickedState.toLowerCase(),
                );
              }

              // If no logo marker, show "We're building..." message
              if (!hasMarker) {
                return (
                  <div className="space-y-6">
                    <div className="text-center py-6">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full flex items-center justify-center">
                        <Heart className="w-8 h-8 text-primary" />
                      </div>
                      <p className="text-lg font-medium text-foreground mb-2">
                        {selectedRegion?.clickedState || selectedRegion?.name}
                      </p>
                      <p className="text-muted-foreground mb-4">
                        We're building toward our first fellowship in this state. Pray with us and join the beginning!
                      </p>
                      <Button className="text-white hover:bg-white hover:text-primary hover:border-primary" asChild>
                        <a href="/contact">Get Involved</a>
                      </Button>
                    </div>

                    {/* Show Universities for this state if any */}
                    {filteredUniversities.length > 0 && (
                      <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                        <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                          <University className="w-4 h-4" />
                          Universities in {clickedState}
                        </h4>
                        <div className="grid gap-3">
                          {filteredUniversities.map((university: any, idx: number) => (
                            <div key={idx} className="p-3 bg-background rounded-lg space-y-1">
                              <p className="font-medium text-sm">{university.name}</p>
                              {university.city && (
                                <p className="text-xs text-muted-foreground">
                                  {university.city}, {university.state}
                                </p>
                              )}
                              {university.contact_person && (
                                <p className="text-xs font-medium">{university.contact_person}</p>
                              )}
                              {university.contact_phone && (
                                <p className="text-xs text-primary">{university.contact_phone}</p>
                              )}
                              {university.contact_email && (
                                <p className="text-xs text-primary">{university.contact_email}</p>
                              )}
                              {university.has_fellowship && (
                                <Badge variant="secondary" className="text-[10px]">
                                  Active Fellowship
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Show Bible Studies for this state if any */}
                    {filteredBibleStudies.length > 0 && (
                      <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                        <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                          <School className="w-4 h-4" />
                          Bible Studies in {clickedState}
                        </h4>
                        <div className="grid gap-3">
                          {filteredBibleStudies.map((study: any, idx: number) => (
                            <div key={idx} className="p-3 bg-background rounded-lg space-y-1">
                              <p className="font-medium text-sm">{study.city}</p>
                              <p className="text-xs text-muted-foreground">{study.type}</p>
                              <p className="text-xs font-medium">{study.contact_name}</p>
                              <p className="text-xs text-primary">{study.contact_phone}</p>
                              {study.contact_email && <p className="text-xs text-primary">{study.contact_email}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Show regional coordinators if available */}
                    {regionData?.coordinators && regionData.coordinators.length > 0 && (
                      <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                        <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Regional Coordinators
                        </h4>
                        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 p-2">
                          {regionData.coordinators.map((coord: any) => (
                            <div key={coord.role} className="flex flex-col items-center gap-1 text-center">
                              {coord.profile_image_url ? (
                                <img
                                  src={coord.profile_image_url}
                                  alt={coord.full_name}
                                  className="w-8 h-8 rounded-full object-cover border-2 border-primary/20"
                                />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-medium">
                                  {coord.full_name
                                    .split(" ")
                                    .map((n: string) => n[0])
                                    .join("")}
                                </div>
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-[10px] text-foreground leading-tight">
                                  {coord.full_name}
                                </p>
                                <p className="text-[9px] text-muted-foreground capitalize leading-tight">
                                  {coord.role.replace(/_/g, " ").replace("regional ", "")}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Has marker - show full region details
              if (
                !regionData ||
                (!regionData.coordinator_name && !regionData.total_members && !regionData.coordinator_profile)
              ) {
                return (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No detailed information available for this region yet.</p>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  {/* Regional Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{regionData.total_members || 0}</div>
                      <div className="text-sm text-muted-foreground">Members</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-secondary/5 to-primary/5 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{regionData.universities || 0}</div>
                      <div className="text-sm text-muted-foreground">Universities</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg">
                      <div className="text-2xl font-bold text-primary">{regionData.active_prayer_cells || 0}</div>
                      <div className="text-sm text-muted-foreground">Prayer Cells</div>
                    </div>
                  </div>

                  {/* Universities for this state */}
                  {filteredUniversities.length > 0 && (
                    <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                      <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                        <University className="w-4 h-4" />
                        Universities {clickedState ? `in ${clickedState}` : ""}
                      </h4>
                      <div className="grid gap-3">
                        {filteredUniversities.map((university: any, idx: number) => (
                          <div key={idx} className="p-3 bg-background rounded-lg space-y-1">
                            <p className="font-medium text-sm">{university.name}</p>
                            {university.city && (
                              <p className="text-xs text-muted-foreground">
                                {university.city}, {university.state}
                              </p>
                            )}
                            {university.contact_person && (
                              <p className="text-xs font-medium">{university.contact_person}</p>
                            )}
                            {university.contact_phone && (
                              <p className="text-xs text-primary">{university.contact_phone}</p>
                            )}
                            {university.contact_email && (
                              <p className="text-xs text-primary">{university.contact_email}</p>
                            )}
                            {university.has_fellowship && (
                              <Badge variant="secondary" className="text-[10px]">
                                Active Fellowship
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Bible Studies for this state */}
                  {filteredBibleStudies.length > 0 && (
                    <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                      <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                        <School className="w-4 h-4" />
                        Bible Studies {clickedState ? `in ${clickedState}` : ""}
                      </h4>
                      <div className="grid gap-3">
                        {filteredBibleStudies.map((study: any, idx: number) => (
                          <div key={idx} className="p-3 bg-background rounded-lg space-y-1">
                            <p className="font-medium text-sm">{study.city}</p>
                            <p className="text-xs text-muted-foreground">{study.type}</p>
                            <p className="text-xs font-medium">{study.contact_name}</p>
                            <p className="text-xs text-primary">{study.contact_phone}</p>
                            {study.contact_email && <p className="text-xs text-primary">{study.contact_email}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Regional Coordinators */}
                  {regionData.coordinators && regionData.coordinators.length > 0 && (
                    <div className="p-4 border rounded-xl bg-gradient-to-br from-background to-muted/30 shadow-sm">
                      <h4 className="font-semibold mb-3 text-primary flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Regional Coordinators
                      </h4>
                      <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2 p-2">
                        {regionData.coordinators.map((coord: any) => (
                          <div key={coord.role} className="flex flex-col items-center gap-1 text-center">
                            {coord.profile_image_url ? (
                              <img
                                src={coord.profile_image_url}
                                alt={coord.full_name}
                                className="w-8 h-8 rounded-full object-cover border-2 border-primary/20"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-medium">
                                {coord.full_name
                                  .split(" ")
                                  .map((n: string) => n[0])
                                  .join("")}
                              </div>
                            )}
                            <div className="flex-1">
                              <p className="font-medium text-[10px] text-foreground leading-tight">{coord.full_name}</p>
                              <p className="text-[9px] text-muted-foreground capitalize leading-tight">
                                {coord.role.replace(/_/g, " ").replace("regional ", "")}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Get Involved Button */}
                  <div className="flex justify-center pt-2">
                    <Button
                      className="bg-primary text-white hover:bg-white hover:text-primary hover:border-primary"
                      asChild
                    >
                      <a href="/contact">Get Involved</a>
                    </Button>
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
      {/* Enhanced Stats Header */}
      {showStatsHeader && (
        <motion.div
          className="mb-8 mt-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            ></motion.div>

            <motion.div
              className="flex flex-wrap justify-center gap-6 md:gap-8 pb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {[
                {
                  icon: <Globe className="w-6 h-6" />,
                  number: "50",
                  label: "Nations",
                  color: "from-emerald-500 to-cyan-500",
                },
                {
                  icon: <University className="w-6 h-6" />,
                  number: "7000+",
                  label: "Post Secondary Educational Institutions",
                  color: "from-purple-500 to-rose-500",
                },
                {
                  icon: <Heart className="w-6 h-6" />,
                  number: "1.1M",
                  label: "International Students",
                  color: "from-amber-500 to-orange-500",
                },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 bg-gradient-to-br from-background to-muted/30 px-6 py-4 rounded-xl border shadow-sm"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-lg`}
                  >
                    {stat.icon}
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-foreground">{stat.number}</div>
                    <div className="text-sm text-muted-foreground font-medium">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default EnhancedInteractiveMap;
